/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import type { Unsubscribe } from '@amazon/vinyl-util'
import type { QueueRestoreDeps } from '@/cloudQueue/queueRestore'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('initializeQueueRestoreLazy')

/**
 * Lazy — the reload queue-recovery module (and the cloudQueue service it
 * dynamic-imports) load off the boot path; queue persistence only matters
 * once a queue exists, well after first paint. The returned disposer wins
 * either race: called before the load resolves it cancels the install,
 * called after it tears down the installed subscriptions through the
 * captured cell.
 */
export function initializeQueueRestoreLazy(deps: QueueRestoreDeps): Unsubscribe {
    let restoreDispose: (() => void) | null = null
    let disposed = false
    import('@/cloudQueue/queueRestore')
        .then(({ initializeQueueRestore }) => {
            if (disposed) return
            restoreDispose = initializeQueueRestore(deps)
        })
        .catch(errorHandler)
    return () => {
        disposed = true
        restoreDispose?.()
    }
}
